/**
 * B606 (d) — the storefront ticket page's pure half. The ticket email links
 * to `/t#<token>` (one ticket) or `/t#<tok1>,<tok2>,…` (its one button,
 * capped at TICKETS_PAGE_TOKEN_CAP); the fragment never reaches a server.
 * Each token is opened through the PUBLIC `ticket.open` door, one call each.
 */
import { buyerTicketStatus } from "./confirmation.js";

/** Mirrors the domain's cap — the email's button names at most this many tickets. */
export const TICKETS_PAGE_TOKEN_CAP = 10;

interface Money {
  readonly amount: number;
  readonly currency: string;
}

/** `ticket.open`'s page — one ticket and its order's summary (never the buyer). */
export interface TicketPageAnswer {
  readonly seller?: string;
  readonly status: string;
  readonly ticket: { readonly label: string; readonly when?: string };
  readonly order?: {
    readonly lines: readonly { readonly label: string; readonly quantity: number; readonly unitPrice: Money; readonly net: Money }[];
    readonly fees: readonly { readonly label: string; readonly amount: Money }[];
    readonly tax?: Money;
    readonly total?: Money;
    readonly confirmationCode?: string;
  };
}

/** One token's face: the answer once it opens, the QR drawn from the token itself. Absent page = it did not open. */
export interface TicketFace {
  readonly token: string;
  readonly page?: TicketPageAnswer;
  readonly qr?: string;
}

/** `/t#a,b` (or `/t/#…`) → the tokens, in order, once each, at most 10; anything else is not a ticket link. */
export function ticketTokensFrom(location: { readonly pathname: string; readonly hash: string }): readonly string[] | undefined {
  if (location.pathname !== "/t" && location.pathname !== "/t/") return undefined;
  const tokens = [...new Set(location.hash.replace(/^#/, "").split(",").map((s) => s.trim()).filter((s) => s.length > 0))];
  return tokens.length === 0 ? undefined : tokens.slice(0, TICKETS_PAGE_TOKEN_CAP);
}

/** The tenant a ttk1 token NAMES — read to claim it, never trusted (the server verifies). */
export function tenantOfTicketToken(token: string): string | undefined {
  const parts = token.split(".");
  if (parts.length !== 4 || parts[0] !== "ttk1") return undefined;
  try {
    const b64 = parts[1]!.replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4)), (c) => c.charCodeAt(0)))) as unknown;
    const t = typeof json === "object" && json !== null ? (json as Record<string, unknown>)["tenantId"] : undefined;
    return typeof t === "string" && t.length > 0 ? t : undefined;
  } catch {
    return undefined;
  }
}

/** The wallet's own words for a ticket's state (B448's table). */
export function ticketStatusWord(state: string): string {
  return buyerTicketStatus(state) ?? "Valid";
}

/** A ticket that can be scanned: shown with its QR. Cancelled / used / expired ones are not offered to the gate. */
export function scannable(state: string): boolean {
  return state === "ISSUED" || state === "ACTIVE";
}

/** Tickets the order holds beyond those this link carries (the button stops at 10). */
export function moreTicketsInEmail(orderQuantity: number, shown: number): number {
  return Math.max(0, orderQuantity - shown);
}

/** The browser tab's words (the index ships "Checkout" — wrong on this page). */
export function ticketPageTitle(seller: string | undefined): string {
  return seller === undefined ? "Your tickets" : `Your tickets — ${seller}`;
}
